import { createRequestParams } from "../helpers/network";
import { setErrorNotification } from "./notifications";
import { sortByKey } from "../helpers/presentation";
import {ACCOUNTS_LIMIT} from "../helpers/accounts";

import { receiveAccounts } from "./accounts";
import { fetchTimeSeries, fetchAccountSeries } from "./usage";

import { generateDefaultPeriod } from "../reducers/overviewFilters";

export const SET_OV_FILTER_VISIBILITY = "SET_OV_FILTER_VISIBILITY";
export const SET_OV_FILTER_PERIOD = "SET_OV_FILTER_PERIOD";
export const SET_OV_FILTER_INSTANCE_TYPE = "SET_OV_FILTER_INSTANCE_TYPE";
export const SET_OV_FILTER_SELECTED = "SET_OV_FILTER_SELECTED";
export const SET_OV_FILTER_ALL = "SET_OV_FILTER_ALL";
export const SET_EXCLUDE_ZERO_COMPUTE = 'SET_EXCLUDE_ZERO_COMPUTE';
export const SET_MIN_STORAGE_COST_ENABLED = 'SET_MIN_STORAGE_COST_ENABLED';
export const SET_MIN_STORAGE_COST = 'SET_MIN_STORAGE_COST';

export const setExcludeZeroComputeAction = (exclude) => {
  return {
    type: SET_EXCLUDE_ZERO_COMPUTE,
    payload: exclude
  };
};

export const setMinStorageCostEnabledAction = (enabled) => {
  return {
    type: SET_MIN_STORAGE_COST_ENABLED,
    payload: enabled
  };
};

export const setMinStorageCostAction = (cost) => {
  return {
    type: SET_MIN_STORAGE_COST,
    payload: cost
  };
};

const createIdTextPair = (id, entities, field) => {
  const empty = { text: "", id: "" };
  if (!id) {
    return empty;
  }
  const obj = entities[id];
  if (!obj) {
    return empty;
  }
  return { id, text: obj[field] };
};

export const setOverviewFiltersActionCreator = (filters, state) => {
  const { entities } = state;
  const { accounts } = entities;
  const { start, end, granularity } = filters;

  /*
    if a project is specified, then since a project must belong to ONE account,
    we override the account given in the url.. if any. This will cause the 
    selection in the account autosuggest box to be set correctly.
  */
  const account = filters.account;

  return {
    type: SET_OV_FILTER_ALL,
    account: createIdTextPair(account, accounts, "name"),
    start,
    end,
    granularity
  };
};

const hasAccounts = getState => {
  const { entities } = getState();
  const { accounts } = entities;
  return Object.keys(accounts).length > 0;
};

function getAccounts() {
  return (dispatch, getState) => {
    const params = { path: "/accounts", limit: ACCOUNTS_LIMIT };
    const { url, options } = createRequestParams(getState(), params);

    return fetch(url, options)
      .then(
        response => {
          return response.json();
        },
        error => {
          return dispatch(setErrorNotification(error, { url, options }));
        }
      )
      .then(json => {
        return dispatch(receiveAccounts(json));
      })
      .catch(function (error) {
        return dispatch(setErrorNotification(error, { url, options }));
      });
  };
}

const getTabLabel = state => {
  const { route } = state;
  return route.overviewTabLabels[route.overviewTabIndex];
};

const valueChanged = (next, prev) => {
  if (!next) {
    next = false;
  }
  if (!prev) {
    prev = false;
  }

  return next !== prev;
};

const periodChanged = (nextPeriod, prevPeriod) => {
  const defaultPeriod = generateDefaultPeriod();

  const prevStart = prevPeriod.start;
  const prevEnd = prevPeriod.end;

  let { start, end } = nextPeriod;

  start = start || defaultPeriod.start;
  end = end || defaultPeriod.end;

  if (!(prevStart.isSame(start, "day") && prevEnd.isSame(end, "day"))) {
    return true;
  }
  return false;
};

const needsFreshFetch = (filters, state) => {
  const existingFilters = state.overview.filters;
  const isInMonitoringMode = state.ui.focused;

  if (isInMonitoringMode && !state.overview.timeSeries.loading) {
    return true;
  }

  if (valueChanged(filters.account, existingFilters.account.selected.id)) {
    return true;
  }

  if (periodChanged(filters, existingFilters.period)) {
    return true;
  }

  return false;
};

function setFiltersAndFetchSeries(filters) {
  return (dispatch, getState) => {
    const state = getState();
    const label = getTabLabel(state);
    const isInMonitoringMode = state.ui.focused;
    const needsFetch =
      (label === "accounts" && !isInMonitoringMode) ||
      needsFreshFetch(filters, state);
    const res = dispatch(setOverviewFiltersActionCreator(filters, state));

    if (!needsFetch) {
      return res;
    }

    switch (label) {
      case "accounts":
        return dispatch(fetchAccountSeries());
      default:
        return dispatch(fetchTimeSeries());
    }
  };
}

export const setOverviewFilters = filters => {
  return (dispatch, getState) => {
    if (hasAccounts(getState)) {
      return dispatch(setFiltersAndFetchSeries(filters));
    }
    return dispatch(getAccounts()).then(() => {
      dispatch(setFiltersAndFetchSeries(filters));
    });
  };
};

export const setOverviewFilterVisibility = visible => ({
  type: SET_OV_FILTER_VISIBILITY,
  visible
});

/* INPUT_VALUE & SUGGESTIONS */

export const GET_OV_FILTER_SUGGESTIONS = "GET_OV_FILTER_SUGGESTIONS";
export const CLEAR_OV_FILTER_SUGGESTIONS = "CLEAR_OV_FILTER_SUGGESTIONS";
export const SET_OV_FILTER_INPUT_VALUE = "SET_OV_FILTER_INPUT_VALUE";

export const setInputValue = (filterKey, value) => ({
  type: SET_OV_FILTER_INPUT_VALUE,
  filterKey,
  value
});

export const getSuggestionsCreator = (filterKey, suggestions) => ({
  type: GET_OV_FILTER_SUGGESTIONS,
  filterKey,
  suggestions
});

export const clearSuggestions = filterKey => ({
  type: CLEAR_OV_FILTER_SUGGESTIONS,
  filterKey
});

/*
  Below are functions to fetch suggestions for autoSuggest. Each successive fetch
  request configuration may depend on the select value in a different (previous)
  autoSuggest field. For example, the list of possible projects will
  depend on the selected account.
*/

export const getSuggestions = {
  account: params => {
    return (dispatch, getState) => {
      const { value } = params;
      const val = value.trim().toLowerCase();
      const len = val.length;
      const state = getState();
      const { accounts } = state.entities;
      let suggestions = [];

      suggestions = Object.keys(accounts)
        .filter(id => accounts[id].name.toLowerCase().slice(0, len) === val)
        .map(id => ({
          text: accounts[id].name,
          id
        }));
      suggestions = sortByKey(suggestions, "text");

      return dispatch(getSuggestionsCreator("account", suggestions));
    };
  }
};



// WEBPACK FOOTER //
// ./src/actions/overviewParams.js